import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Crown, Sparkles, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePublicAuth } from '../context/PublicAuthContext';
import { useMerchCart } from '../context/MerchCartContext';
import { aurasApi } from '../lib/api';
import NotificationBell from './NotificationBell';

const MerchCartButton = () => {
  const cart = useMerchCart();
  if (!cart || cart.count === 0) return null;
  return (
    <button onClick={cart.openCart} className="relative text-white/70 hover:text-[#D4AF37] transition-colors" data-testid="nav-cart-btn">
      <ShoppingBag size={19} />
      <span className="absolute -top-1.5 -right-2 bg-[#D4AF37] text-black text-[9px] font-black rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{cart.count}</span>
    </button>
  );
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = usePublicAuth();
  const [auraColors, setAuraColors] = useState([]);

  const isBravxPage = location.pathname === '/fullbravx';

  // Allow other components (e.g. MobileBottomNav) to open the same mobile menu
  // drawer this Navbar owns. Keeps state ownership here and avoids prop drilling.
  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    window.addEventListener('open-mobile-menu', onOpen);
    return () => window.removeEventListener('open-mobile-menu', onOpen);
  }, []);

  // Load user's aura colors
  useEffect(() => {
    const loadAura = async () => {
      if (user?.selected_aura && user.selected_aura !== 'off') {
        try {
          const res = await aurasApi.getPresets();
          const selectedAura = res.data?.find(a => a.id === user.selected_aura);
          if (selectedAura?.colors?.length) {
            setAuraColors(selectedAura.colors);
          }
        } catch (e) {
          console.error('Error loading aura:', e);
        }
      }
    };
    loadAura();
  }, [user?.selected_aura]);

  // Desktop nav links
  const navLinks = [
    { href: '/', label: 'Inicio' },
    { href: '/eventos', label: 'Eventos' },
    { href: '/tienda', label: 'Merch' },
    { type: 'community', label: 'Comunidad' },
    { href: '/quienes-somos', label: 'Nosotros' },
    { href: '/fullbravx', label: 'BRAVX', isPremium: true },
  ];

  // Mobile nav links - BRAVX in the middle
  const mobileNavLinks = [
    { href: '/', label: 'Inicio' },
    { href: '/eventos', label: 'Eventos' },
    { href: '/fullbravx', label: 'BRAVX', isPremium: true },
    { href: '/tienda', label: 'Merch' },
    { href: '/quienes-somos', label: 'Nosotros' },
  ];

  const isActive = (path) => location.pathname === path;

  // Generate aura gradient for avatar
  const getAuraGradient = () => {
    if (auraColors.length >= 2) {
      return `linear-gradient(135deg, ${auraColors[0]}, ${auraColors[1]}, ${auraColors[2] || auraColors[0]})`;
    }
    return 'linear-gradient(135deg, #27272A, #3F3F46)';
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-colors duration-300 hidden md:block ${
      isBravxPage 
        ? 'bg-[#0A0A0A]/95 border-yellow-500/20' 
        : 'bg-[#0A0A0A]/95 border-[#27272A]'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center py-2" data-testid="nav-logo">
            <motion.img 
              src={LOGO_URL} 
              alt="Bravo Producciones" 
              className="w-28 md:w-32 lg:w-36 h-auto object-contain"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-10 lg:space-x-12">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.href || link.type}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                {link.type === 'community' ? (
                  /* Community drawer trigger */
                  <button
                    onClick={() => { try { window.dispatchEvent(new CustomEvent('open-community-drawer')); } catch (_) {} }}
                    data-testid="nav-comunidad"
                    className="relative text-sm font-medium uppercase tracking-widest text-[#A1A1AA] hover:text-white transition-colors duration-300 group"
                  >
                    {link.label}
                    <span className="absolute -bottom-1 left-0 h-[2px] bg-white transition-all duration-300 w-0 group-hover:w-full" />
                  </button>
                ) : link.isPremium ? (
                  /* Premium BRAVX Button */
                  <Link
                    to={link.href}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className="relative group"
                  >
                    <motion.div
                      className={`relative px-4 py-2 rounded-full border-2 transition-all duration-300 ${
                        isActive(link.href)
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 border-yellow-400 shadow-lg shadow-yellow-500/30'
                          : 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/50 hover:border-yellow-400 hover:shadow-lg hover:shadow-yellow-500/20'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center gap-2">
                        <Crown size={14} className={isActive(link.href) ? 'text-black' : 'text-yellow-400'} />
                        <span className={`text-sm font-bold uppercase tracking-wider ${
                          isActive(link.href) ? 'text-black' : 'text-yellow-400'
                        }`}>
                          {link.label}
                        </span>
                      </div>
                      {/* Glow effect */}
                      {!isActive(link.href) && (
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
                      )}
                    </motion.div>
                  </Link>
                ) : (
                  /* Regular Nav Link */
                  <Link
                    to={link.href}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className={`relative text-sm font-medium uppercase tracking-widest transition-colors duration-300 group ${
                      isActive(link.href)
                        ? 'text-white'
                        : 'text-[#A1A1AA] hover:text-white'
                    }`}
                  >
                    {link.label}
                    <span className={`absolute -bottom-1 left-0 h-[2px] bg-white transition-all duration-300 ${
                      isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'
                    }`} />
                  </Link>
                )}
              </motion.div>
            ))}
            
            {/* Merch Cart */}
            <MerchCartButton />

            {/* Notification Bell */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: navLinks.length * 0.1 }}
            >
              <NotificationBell isAuthenticated={isAuthenticated} />
            </motion.div>
            
            {/* User Avatar or Mi Cuenta */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: (navLinks.length + 1) * 0.1 }}
            >
              <Link
                to="/mi-cuenta"
                data-testid="nav-mi-cuenta"
                className="relative group"
              >
                {isAuthenticated && user ? (
                  <div className="relative">
                    {/* Aura glow effect */}
                    {auraColors.length > 0 && (
                      <div 
                        className="absolute inset-0 rounded-full blur-md opacity-60 group-hover:opacity-80 transition-opacity"
                        style={{ background: getAuraGradient(), transform: 'scale(1.3)' }}
                      />
                    )}
                    {/* Avatar */}
                    <div 
                      className="relative w-9 h-9 rounded-full flex items-center justify-center overflow-hidden border-2 transition-all duration-300"
                      style={{ 
                        borderColor: auraColors[0] || '#27272A',
                        background: user.profile_picture ? 'transparent' : getAuraGradient()
                      }}
                    >
                      {user.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt={user.full_name || 'Usuario'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={18} className="text-white" />
                      )}
                    </div>
                  </div>
                ) : (
                  <span className={`text-sm font-medium uppercase tracking-widest transition-colors duration-300 ${
                    isActive('/mi-cuenta') ? 'text-white' : 'text-[#A1A1AA] hover:text-white'
                  }`}>
                    Mi Cuenta
                  </span>
                )}
              </Link>
            </motion.div>
          </div>

          {/* Mobile Notification Bell + Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <NotificationBell isAuthenticated={isAuthenticated} />
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-white"
              data-testid="mobile-menu-toggle"
              whileTap={{ scale: 0.95 }}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`md:hidden border-b ${
              isBravxPage ? 'bg-[#0A0A0A] border-yellow-500/20' : 'bg-[#0A0A0A] border-[#27272A]'
            }`}
          >
            <div className="px-6 py-8 space-y-6">
              {mobileNavLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  {link.isPremium ? (
                    /* Premium BRAVX in Mobile */
                    <Link
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className="block"
                    >
                      <div className={`inline-flex items-center gap-3 px-5 py-3 rounded-full border-2 transition-all ${
                        isActive(link.href)
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 border-yellow-400'
                          : 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/50'
                      }`}>
                        <Crown size={18} className={isActive(link.href) ? 'text-black' : 'text-yellow-400'} />
                        <span className={`text-lg font-bold uppercase tracking-widest ${
                          isActive(link.href) ? 'text-black' : 'text-yellow-400'
                        }`}>
                          {link.label}
                        </span>
                        <Sparkles size={14} className={isActive(link.href) ? 'text-black' : 'text-yellow-400/60'} />
                      </div>
                    </Link>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`block text-lg font-medium uppercase tracking-widest ${
                        isActive(link.href) ? 'text-white' : 'text-[#A1A1AA]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </motion.div>
              ))}
              
              {/* Mobile Mi Cuenta */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: navLinks.length * 0.1 }}
              >
                <Link
                  to="/mi-cuenta"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 text-lg font-medium uppercase tracking-widest ${
                    isActive('/mi-cuenta') ? 'text-white' : 'text-[#A1A1AA]'
                  }`}
                >
                  {isAuthenticated && user ? (
                    <>
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border-2"
                        style={{ 
                          borderColor: auraColors[0] || '#27272A',
                          background: user.profile_picture ? 'transparent' : getAuraGradient()
                        }}
                      >
                        {user.profile_picture ? (
                          <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User size={16} className="text-white" />
                        )}
                      </div>
                      <span>{user.full_name?.split(' ')[0] || 'Mi Cuenta'}</span>
                    </>
                  ) : (
                    'Mi Cuenta'
                  )}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
